import {Component, DestroyRef, inject, OnInit, ViewEncapsulation} from '@angular/core';
import {MatButton} from '@angular/material/button';
import {MatFormField, MatHint, MatLabel, MatSuffix} from '@angular/material/form-field';
import {MatInput} from '@angular/material/input';
import {MatOption} from '@angular/material/autocomplete';
import {MatSelect} from '@angular/material/select';
import {PaginatorModule} from 'primeng/paginator';
import {FormBuilder, ReactiveFormsModule, Validators} from '@angular/forms';
import {AppStateService} from '../../../service/app-state.service';
import {CantaaMessageService} from '../../../service/cantaa-message.service';
import {ConsoleLoggerService} from '../../../service/console-logger.service';
import {CantaaErrorHandlerService} from '../../../service/cantaa-error-handler.service';
import {NodeType} from '../../../constants/node-type';
import {WimTreeNode} from '../../../model/wim-tree.node';
import {ActivatedRoute, Router} from '@angular/router';
import {RouteHelper} from '../../../service/helper/route-helper';
import {Permission} from '../../../constants/Permission';
import {HasPermissionDirective} from '../../../directive/has-permission.directive';
import {LoggedUserService} from '../../../service/logged-user.service';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {ShowIfDebugMode} from "../../../directive/show-if-debug.directive";
import {ProjectService} from '../../../service/project.service';
import {ProjectDetailItem} from '../../../model/project-detail.item';
import {ProjectTypeListItem} from '../../../model/project-type.model';
import {MatDatepicker, MatDatepickerInput, MatDatepickerToggle} from '@angular/material/datepicker';
import {ProjectTypeService} from '../../../service/project-type.service';
import { ConfirmationService } from 'primeng/api';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
// import { MessageService } from 'primeng/api';

@Component({
  selector: 'wim-project-detail',
  standalone: true,
  imports: [
    MatButton,
    MatFormField,
    MatInput,
    MatLabel,
    MatOption,
    MatSelect,
    PaginatorModule,
    ReactiveFormsModule,
    HasPermissionDirective,
    TranslateModule,
    ShowIfDebugMode,
    MatDatepicker,
    MatDatepickerInput,
    MatDatepickerToggle,
    MatHint,
    MatSuffix,
    ConfirmDialogModule
  ],
  providers: [ConfirmationService],
  templateUrl: './project-detail.component.html',
  styleUrl: './project-detail.component.scss',
  encapsulation: ViewEncapsulation.None
})
export class ProjectDetailComponent implements OnInit {

  protected readonly Permission = Permission;
  protected readonly destroyRef = inject(DestroyRef);
  protected readonly translate = inject(TranslateService);
  protected readonly confirmationService = inject(ConfirmationService);

  projectTypes: ProjectTypeListItem[] = [];

  detailForm = this.fb.group({
    id: this.fb.control<number | null>({ value: null, disabled: true }),
    projectTypeId: undefined,
    projectType: this.fb.control<ProjectTypeListItem | null>(null, Validators.required),
    number: ['', [Validators.required]],
    startDate: (''),
    terminationDate: (''),
    status: ['', [Validators.required]]
  });

  constructor(private fb: FormBuilder,
              private appStateService: AppStateService,
              private projectService: ProjectService,
              private projectTypeService: ProjectTypeService,
              private messageService: CantaaMessageService,
              private log: ConsoleLoggerService,
              private errorHandler: CantaaErrorHandlerService,
              private route: ActivatedRoute,
              private router: Router,
              private loggedUserService: LoggedUserService) {
  }

  async ngOnInit() {

    if (RouteHelper.isInNewState(this.route)) {
      this.detailForm.patchValue({ status: 'ACTIVE' }); // oder was sinnvoll ist
    }

    this.projectTypes = await this.fetchProjectTypes();

    this.detailForm.get('projectType')!.valueChanges.subscribe(projectType => {
      this.detailForm.patchValue({ projectTypeId: projectType?.id ?? null }, { emitEvent: false });
    });

    const onSelectNodeSubscription = this.appStateService.getSelectedNode$()
      .subscribe(async selectedNode => {
        if (!selectedNode.id || selectedNode.nodeType != NodeType.PROJECT) {
          return;
        }
        await this.initDetail(selectedNode);
      });

    this.destroyRef.onDestroy(() => {
      onSelectNodeSubscription.unsubscribe();
    });
  }

  private async initDetail(selectedNode: WimTreeNode) {
    const detail = await this.fetchDetail(selectedNode);
    this.setDetailToForm(detail);

    if (!this.loggedUserService.hasPermission(Permission.PROJECT_EDIT)) {
      this.detailForm.disable();
    }
  }

  private async fetchDetail(selectedNode: WimTreeNode) {
    try {
      return await this.projectService.getProjectDetail(selectedNode.id!);
    } catch (e) {
      this.errorHandler.handleError(e, 'FAILED_TO_FETCH Project Detail');
      throw e;
    }
  }

  private setDetailToForm(detail: ProjectDetailItem) {

    let projectType = this.projectTypes.find(t => t.id === detail.projectTypeId) ?? null;
    this.detailForm.reset(
      {
        id: detail.id,
        projectTypeId: detail.projectTypeId,
        projectType: projectType,
        number: detail.number,
        startDate: detail.startDate,
        terminationDate: detail.terminationDate,
        status: detail.status
      }
    );
  }

  async onSave() {
    this.log.debug('on save', this.detailForm.value);

    const detail = {
      id: this.detailForm.getRawValue().id,
      projectTypeId: this.detailForm.getRawValue().projectTypeId,
      number: this.detailForm.getRawValue().number,
      startDate: this.detailForm.getRawValue().startDate,
      terminationDate: this.detailForm.getRawValue().terminationDate,
      status: this.detailForm.getRawValue().status
    } as ProjectDetailItem;

    try {
      await this.projectService.saveProject(detail);
      this.messageService.success('SAVE.SUCCESS');

      if (RouteHelper.isInNewState(this.route)) {
        await RouteHelper.navigateToGrid(this.route, this.router);
      } else {
        this.detailForm.markAsPristine();
        this.detailForm.markAsUntouched();
      }
    } catch (e) {
      this.errorHandler.handleError(e, 'SAVE.FAILED');
    }
  }

  async onCancel() {
    if (RouteHelper.isInNewState(this.route)) {
      this.detailForm.reset();
    } else {
      let selectedNodeValue = this.appStateService.selectedNodeValue;
      await this.initDetail(selectedNodeValue);
    }
  }

    onProjectTypeChange(locationType: ProjectTypeListItem | null) {

    }

  private async fetchProjectTypes() {
    try {
      return await this.projectTypeService.getProjectTypes();
    } catch (e) {
      this.errorHandler.handleError(e, 'FAILED_TO_FETCH');
      throw e;
    }
  }

  get localizedStatus(): string {
    const status = this.detailForm.get('status')?.value;
    return status ? this.translate.instant(status) : '';
  }

  get statusAction(): 'TERMINATE' | 'REACTIVATE' {
    const status = this.detailForm.get('status')?.value;
    return status === 'ACTIVE' ? 'TERMINATE' : 'REACTIVATE';
  }

  switchStatus(action: 'TERMINATE' | 'REACTIVATE') {
    const statusControl = this.detailForm.get('status');
    const terminationDateControl = this.detailForm.get('terminationDate');
    const today = new Date().toISOString().substring(0, 10);

    const finalize = () => {
      statusControl?.setValue(action === 'TERMINATE' ? 'TERMINATED' : 'ACTIVE');
      this.onSave();
    };

    if (action === 'TERMINATE') {
      const existingDate = terminationDateControl?.value;

      if (!existingDate || existingDate === today) {
        terminationDateControl?.setValue(today);
        finalize();
      } else {
        this.confirmationService.confirm({
          message: this.translate.instant('PROJECT.SETTERMINATIONDATE'),
          header: this.translate.instant('CONFIRMATION.CONFIRMATION'),
          icon: 'pi pi-exclamation-triangle',
          acceptLabel: this.translate.instant('BOOLEAN.YESNO.TRUE'),
          rejectLabel: this.translate.instant('BOOLEAN.YESNO.FALSE'),
          accept: () => {
            terminationDateControl?.setValue(today);
            finalize();
          },
          reject: () => {
            finalize();
          }
        });
      }
    } else {
      finalize();
    }
  }

}
